/**
 * Whitelist of core Claim columns that a product version's claim form fields
 * may be mapped onto (docs/plan/product-version-form-mapping/plan.md).
 *
 * Keys deliberately equal the Claim column names so the future
 * mapping-application engine can assign values directly. Extend consciously —
 * every key added here becomes a user-visible mapping target and a column the
 * lodgement engine must be able to write.
 */

export type MappableClaimFieldValueType = 'text' | 'number' | 'date' | 'boolean' | 'address';

export type MappableClaimFieldGroup = 'Loss details' | 'Policy' | 'Other';

export interface MappableClaimField {
    /** Claim column name */
    key: string;
    /** UI label (sentence case) */
    label: string;
    group: MappableClaimFieldGroup;
    valueType: MappableClaimFieldValueType;
}

export const MAPPABLE_CLAIM_FIELDS = [
    // Loss details
    { key: 'claimOccurredAt', label: 'Date of loss', group: 'Loss details', valueType: 'date' },
    { key: 'causeOfLoss', label: 'Cause of loss', group: 'Loss details', valueType: 'text' },
    { key: 'addressLine1OfLoss', label: 'Loss address line 1', group: 'Loss details', valueType: 'text' },
    { key: 'addressLine2OfLoss', label: 'Loss address line 2', group: 'Loss details', valueType: 'text' },
    { key: 'suburbOfLoss', label: 'Loss suburb', group: 'Loss details', valueType: 'text' },
    { key: 'stateOfLoss', label: 'Loss state', group: 'Loss details', valueType: 'text' },
    { key: 'postcodeOfLoss', label: 'Loss postcode', group: 'Loss details', valueType: 'text' },
    // Policy
    { key: 'policyNo', label: 'Policy number', group: 'Policy', valueType: 'text' },
    { key: 'policyName', label: 'Policy name', group: 'Policy', valueType: 'text' },
    { key: 'policyTermFrom', label: 'Policy term from', group: 'Policy', valueType: 'date' },
    { key: 'policyTermTo', label: 'Policy term to', group: 'Policy', valueType: 'date' },
    { key: 'policyIssuedOn', label: 'Policy issued on', group: 'Policy', valueType: 'date' },
    { key: 'policyOtherInfo', label: 'Policy other information', group: 'Policy', valueType: 'text' },
    // Policy-holder payee/company details and the broker company are deliberately NOT mappable:
    // products-v2 claims never write `Claim.ph*`, `phCompany` or `brokerCompany` — those facts
    // are contact fields on the primary policy-holder / broker contact (contacts-assessment.md § 5).
    // Other
    { key: 'additionalInfo', label: 'Additional information', group: 'Other', valueType: 'text' },
    // Composite: an address element writes every loss-address column at once.
    { key: 'lossAddress', label: 'Loss address (all parts)', group: 'Loss details', valueType: 'address' },
] as const satisfies readonly MappableClaimField[];

/**
 * How a composite `address` target spreads over Claim columns: address part →
 * column. Every column here is itself a scalar target above, so a text field
 * can still feed one part on its own.
 */
export const CLAIM_ADDRESS_COLUMNS = {
    lossAddress: {
        line1: 'addressLine1OfLoss',
        line2: 'addressLine2OfLoss',
        suburb: 'suburbOfLoss',
        state: 'stateOfLoss',
        postcode: 'postcodeOfLoss',
    },
} as const;

export type ClaimAddressTargetKey = keyof typeof CLAIM_ADDRESS_COLUMNS;

export function isClaimAddressTargetKey(key: string): key is ClaimAddressTargetKey {
    return key in CLAIM_ADDRESS_COLUMNS;
}

/** A Claim column a mapping can write — the scalar targets only; composites expand into them. */
export type MappableClaimColumnKey = Exclude<MappableClaimFieldKey, ClaimAddressTargetKey>;

export type MappableClaimFieldKey = (typeof MAPPABLE_CLAIM_FIELDS)[number]['key'];

export const MAPPABLE_CLAIM_FIELD_KEYS = MAPPABLE_CLAIM_FIELDS.map((field) => field.key) as [
    MappableClaimFieldKey,
    ...MappableClaimFieldKey[],
];

export const MAPPABLE_CLAIM_FIELDS_BY_KEY: Record<MappableClaimFieldKey, MappableClaimField> = Object.fromEntries(
    MAPPABLE_CLAIM_FIELDS.map((field) => [field.key, field]),
) as Record<MappableClaimFieldKey, MappableClaimField>;

export function isMappableClaimFieldKey(key: string): key is MappableClaimFieldKey {
    return key in MAPPABLE_CLAIM_FIELDS_BY_KEY;
}
